/**
 * MessageBubble — Individual message rendering with neubrutalism styling
 *
 * Adapted from bachata-nation-next MessageBubble.tsx.
 * Supports text, media, voice, poll, system, reply threading, reactions, and status.
 */

import { memo, useMemo, useCallback, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue, useTransform, useAnimationControls } from 'motion/react';
import { useAuthStore } from '@/features/auth';
import type { ChatMessage, PollData, Mention } from '../types';
import { MessageStatus } from './MessageStatus';
import type { MessageDeliveryStatus } from './MessageStatus';
import { VoiceMessage } from './VoiceMessage';
import { PollMessage } from './PollMessage';
import { ChatAvatar } from './ChatAvatar';
import { ProgressiveImage } from './ProgressiveImage';
import { PdfThumbnail } from './PdfThumbnail';
import { FileTypeIcon } from './FileTypeIcon';
import { MSG_COLORS } from '@/constants/theme';
import { renderMarkdown } from '../utils/renderMarkdown';
import { chatHaptic } from '../utils/chatHaptic';

const SWIPE_THRESHOLD = 60;

/**
 * Detect emoji-only messages and return count.
 * Returns 0 if the message contains any non-emoji text.
 */
const EMOJI_REGEX = /\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu;
function getEmojiOnlyCount(text: string | undefined | null): number {
  if (!text) return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  // Strip all emoji, ZWJ sequences, variation selectors, skin tone modifiers
  // eslint-disable-next-line no-misleading-character-class
  const stripped = trimmed.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200D\uFE0F\u{1F3FB}-\u{1F3FF}]/gu, '').trim();
  if (stripped.length > 0) return 0; // has non-emoji text
  const emojis = [...trimmed.match(EMOJI_REGEX) || []];
  return emojis.length;
}

/** Derive a single delivery status from ChatMessage fields */
function deriveDeliveryStatus(message: ChatMessage): MessageDeliveryStatus {
  if (message.isFailed) return 'failed';
  if (message.isPending) return 'sending';
  if (message.isRead) return 'read';
  if (message.isDelivered) return 'delivered';
  return 'sent';
}

interface MessageBubbleProps {
  message: ChatMessage;
  showSender?: boolean;
  showAvatar?: boolean;
  isGrouped?: boolean;
  onReply?: (message: ChatMessage) => void;
  onReaction?: (messageId: string, emoji: string, action: 'add' | 'remove') => void;
  onDelete?: (messageId: string) => void;
  onEdit?: (messageId: string, content: string) => void;
  onStar?: (messageId: string, action: 'add' | 'remove') => void;
  onPollVote?: (messageId: string, optionIndex: number) => void;
  onRetry?: (tempId: string) => void;
  onRemoveOptimistic?: (tempId: string) => void;
  onLongPress?: (message: ChatMessage, e: React.MouseEvent | React.TouchEvent) => void;
  onMediaTap?: (messageId: string) => void;
  onOpenThread?: (message: ChatMessage) => void;
  isHighlighted?: boolean;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/**
 * Render message content with markdown + mention highlighting.
 * Emoji-only messages skip formatting.
 */
function renderMessageContent(content: string, mentions?: Mention[] | null, _isMine?: boolean): ReactNode {
  // Apply markdown first
  const markdownNodes = renderMarkdown(content);

  // If no mentions, return markdown nodes directly
  if (!mentions || mentions.length === 0) return markdownNodes;

  // Mentions are already in the content as display names with @prefix
  // The blue highlight is handled via CSS on the mention tokens
  return markdownNodes;
}

function formatTime(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(locale === 'he' ? 'he-IL' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export const MessageBubble = memo(function MessageBubble({
  message,
  showSender = false,
  showAvatar = false,
  isGrouped = false,
  onReply,
  onReaction,
  onDelete: _onDelete,
  onEdit: _onEdit,
  onStar: _onStar,
  onPollVote,
  onRetry,
  onRemoveOptimistic,
  onLongPress,
  onMediaTap,
  onOpenThread,
  isHighlighted = false,
}: MessageBubbleProps) {
  const { t, i18n } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isMine = message.senderId === currentUserId;
  const [showReactions, setShowReactions] = useState(false);

  // Swipe-to-reply gesture
  const isRtl = i18n.dir() === 'rtl';
  const dragX = useMotionValue(0);
  const controls = useAnimationControls();
  const thresholdCrossedRef = useRef(false);

  // In LTR, swipe right to reply (positive x). In RTL, swipe left (negative x).
  // The reply icon progress goes from 0 to 1 based on threshold.
  const replyIconProgress = useTransform(dragX, (x) => {
    const distance = isRtl ? -x : x;
    return Math.min(Math.max(distance / SWIPE_THRESHOLD, 0), 1);
  });
  const replyIconScale = useTransform(replyIconProgress, [0, 1], [0.4, 1]);
  const replyIconOpacity = useTransform(replyIconProgress, [0, 0.3, 1], [0, 0.5, 1]);

  const midThresholdCrossedRef = useRef(false);

  const handleDrag = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number } }) => {
      const distance = isRtl ? -info.offset.x : info.offset.x;
      // 60% midpoint threshold
      if (distance >= SWIPE_THRESHOLD * 0.6 && !midThresholdCrossedRef.current) {
        midThresholdCrossedRef.current = true;
        chatHaptic.swipeProgress();
      } else if (distance < SWIPE_THRESHOLD * 0.6) {
        midThresholdCrossedRef.current = false;
      }
      // Full threshold
      if (distance >= SWIPE_THRESHOLD && !thresholdCrossedRef.current) {
        thresholdCrossedRef.current = true;
        chatHaptic.swipeThreshold();
      } else if (distance < SWIPE_THRESHOLD) {
        thresholdCrossedRef.current = false;
      }
    },
    [isRtl]
  );

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number } }) => {
      const distance = isRtl ? -info.offset.x : info.offset.x;
      if (distance >= SWIPE_THRESHOLD && onReply) {
        chatHaptic.swipeComplete();
        onReply(message);
      }
      thresholdCrossedRef.current = false;
      midThresholdCrossedRef.current = false;
      void controls.start({ x: 0, transition: { type: 'spring', stiffness: 500, damping: 30 } });
    },
    [isRtl, onReply, message, controls]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      chatHaptic.longPress();
      onLongPress?.(message, e);
    },
    [message, onLongPress]
  );

  const _handleTouchStart = useCallback(() => {
    const timer = setTimeout(() => {
      chatHaptic.longPress();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Reaction counts — must be before early returns (rules of hooks)
  const reactionEntries = useMemo(() => {
    return Object.entries(message.reactions || {}).filter(([, users]) => users.length > 0);
  }, [message.reactions]);

  const hasReactions = reactionEntries.length > 0;

  // Detect content text direction (first strong directional character)
  const contentDir = useMemo<'ltr' | 'rtl'>(() => {
    const text = message.content || '';
    for (const char of text) {
      if (/[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(char)) return 'rtl';
      if (/[A-Za-z]/.test(char)) return 'ltr';
    }
    return 'ltr';
  }, [message.content]);

  // Media messages (image/video) get wider bubbles with tight padding like WhatsApp
  const isMediaMessage = (message.type === 'image' || message.type === 'video') && !!message.mediaUrl;
  // Audio messages get WhatsApp-style bubble with own colors & metadata
  const isAudioMessage = message.type === 'audio' && !!message.mediaUrl;
  // Emoji-only messages: 1 emoji = triple, 2 = double, 3+ or mixed = normal
  const emojiOnlyCount = message.type === 'text' ? getEmojiOnlyCount(message.content) : 0;
  const isEmojiOnly = emojiOnlyCount > 0 && emojiOnlyCount <= 2 && !message.replyTo && !message.forwardedFrom;

  // System messages
  if (message.type === 'system') {
    return (
      <div className="my-2 flex justify-center px-4">
        <div
          className="rounded-full border border-white/20 bg-white/30 px-4 py-1 text-xs font-medium text-neutral-500"
          dir="auto"
        >
          {message.content}
        </div>
      </div>
    );
  }

  // Deleted messages
  if (message.isDeleted) {
    return (
      <div dir="ltr" className={`flex px-4 py-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
        <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-xs italic text-neutral-400">
          {t('messaging.messageDeleted')}
        </div>
      </div>
    );
  }

  return (
    <div
      id={`msg-${message.id}`}
      dir="ltr"
      className={`group relative flex px-4 ${isGrouped ? 'py-0.5' : 'py-1'} ${
        isMine ? 'justify-end' : 'justify-start'
      } ${isHighlighted ? 'animate-highlight-flash rounded-2xl' : ''}`}
    >
      {/* Avatar */}
      {!isMine && showAvatar && (
        <div className="me-2 flex-shrink-0">
          <ChatAvatar
            src={message.sender.avatar}
            name={message.sender.name || '?'}
            size="h-8 w-8"
            textSize="text-xs"
          />
        </div>
      )}
      {!isMine && !showAvatar && isGrouped && <div className="me-2 w-8 flex-shrink-0" />}

      <div className={`relative ${
        isMediaMessage
          ? 'w-[85vw] max-w-[330px]'
          : isAudioMessage
          ? 'min-w-[240px] max-w-[320px]'
          : 'max-w-[75%]'
      } ${isMine ? 'items-end' : 'items-start'}`}>
        {/* Reply arrow icon revealed on swipe */}
        {onReply && (
          <motion.div
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${
              isRtl ? 'right-0 translate-x-full pe-2' : 'left-0 -translate-x-full ps-2'
            }`}
            style={{ opacity: replyIconOpacity, scale: replyIconScale }}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/30 text-neutral-800 shadow-sm">
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: isRtl ? 'scaleX(-1)' : undefined }}
              >
                <polyline points="9 17 4 12 9 7" />
                <path d="M20 18v-2a4 4 0 00-4-4H4" />
              </svg>
            </div>
          </motion.div>
        )}

        <motion.div
          style={{ x: dragX }}
          drag={onReply ? 'x' : false}
          dragConstraints={isRtl ? { left: -80, right: 0 } : { left: 0, right: 80 }}
          dragElastic={0.1}
          dragMomentum={false}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          animate={controls}
        >
        {/* Sender name */}
        {showSender && !isMine && (
          <div className="mb-0.5 px-1 text-[11px] font-medium text-neutral-500" dir="auto">
            {message.sender.name}
          </div>
        )}

        {/* Bubble */}
        <div
          className={`relative rounded-2xl text-sm transition-all ${
            isEmojiOnly
              ? 'bg-transparent px-1 py-0.5'
              : isAudioMessage
              ? `p-3 pb-2 shadow-sm ${isMine ? 'rounded-se-none' : 'rounded-ss-none'}`
              : isMediaMessage
              ? `overflow-hidden pt-6 px-2 pb-1 shadow-sm ${isMine ? 'rounded-se-none' : 'rounded-ss-none'}`
              : `px-3 py-2 ${onLongPress && !message.isDeleted ? (isMine ? 'pl-7' : 'pr-7') : ''} ${
                  isMine
                    ? 'border border-white/10 bg-neutral-800/95 text-white shadow-sm backdrop-blur-md backdrop-saturate-150'
                    : 'border shadow-sm backdrop-blur-md backdrop-saturate-150'
                }`
          } ${message.isPending ? 'opacity-70' : ''} ${message.isFailed ? 'border-error-400' : ''}`}
          style={
            !isMine && !isEmojiOnly
              ? isAudioMessage || isMediaMessage
                ? { backgroundColor: 'var(--user-page-card-bg)' }
                : { backgroundColor: 'var(--user-page-card-bg)', borderColor: 'var(--user-page-card-border)', color: 'var(--user-page-heading)' }
              : isMine && (isAudioMessage || isMediaMessage)
                ? { backgroundColor: 'var(--user-page-card-bg)' }
                : undefined
          }
          onClick={() => setShowReactions((prev) => !prev)}
          onContextMenu={handleContextMenu}
        >
          {/* WhatsApp-style chevron dropdown trigger */}
          {onLongPress && !message.isDeleted && !isEmojiOnly && (
            <button
              type="button"
              className={`absolute top-1 ${isMine ? 'left-1' : 'right-1'} z-10 flex h-5 w-5 items-center justify-center rounded-full transition-all active:scale-90 ${
                isMediaMessage || isAudioMessage
                  ? 'text-neutral-400 hover:bg-black/5 hover:text-neutral-600 active:bg-black/10'
                  : isMine
                  ? 'text-white/50 hover:bg-white/15 hover:text-white/80 active:bg-white/25'
                  : 'text-neutral-300 hover:bg-black/5 hover:text-neutral-500 active:bg-black/10'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onLongPress(message, e);
              }}
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
          {/* Reply quote */}
          {message.replyTo && (
            <div
              className={`mb-2 rounded-lg border-s-[3px] px-2 py-1 text-xs ${
                isAudioMessage
                  ? isMine
                    ? 'border-slate-400'
                    : 'border-slate-400 bg-white/40 text-neutral-800/70'
                  : isMine
                    ? 'border-neutral-300 bg-white/10 text-white/80'
                    : 'border-slate-400 bg-white/30 text-neutral-800/70'
              }`}
              style={isAudioMessage && isMine ? { backgroundColor: MSG_COLORS.sentAudioBg, color: MSG_COLORS.sentAudioText } : undefined}
              dir="auto"
            >
              <div className="font-medium">{message.replyTo.senderName}</div>
              <div className="line-clamp-2">{message.replyTo.content || `[${message.replyTo.type}]`}</div>
            </div>
          )}

          {/* Forwarded label */}
          {message.forwardedFrom && (
            <div className={`mb-1 flex items-center gap-1 text-[11px] italic ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 17 20 12 15 7" />
                <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
              </svg>
              <span>{t('messaging.forwardedFrom', { name: message.forwardedFrom.senderName })}</span>
            </div>
          )}

          {/* Content by type */}
          {message.type === 'text' && (() => {
            const emojiCount = getEmojiOnlyCount(message.content);
            if (emojiCount > 0 && emojiCount <= 2 && !message.replyTo) {
              const emojiClass = emojiCount === 1 ? 'text-5xl leading-tight' : 'text-4xl leading-tight';
              return (
                <div className={`whitespace-pre-wrap break-words ${emojiClass}`} dir="auto">
                  {message.content}
                </div>
              );
            }
            return (
              <div className="chat-markdown whitespace-pre-wrap break-words" dir="auto">
                {renderMessageContent(message.content || '', message.mentions, isMine)}
              </div>
            );
          })()}

          {message.type === 'image' && message.mediaUrl && (
            <div className="overflow-hidden rounded-lg">
              <ProgressiveImage
                src={message.mediaUrl}
                thumbnail={message.mediaThumbnail}
                className="aspect-[4/3] w-full object-cover"
                onClick={() => onMediaTap?.(message.id)}
              />
              {message.content && (
                <div className="mt-1 px-2 pb-1 whitespace-pre-wrap break-words text-sm" dir="auto">
                  {message.content}
                </div>
              )}
            </div>
          )}

          {message.type === 'video' && message.mediaUrl && (
            <div className="overflow-hidden rounded-lg">
              <button
                type="button"
                className="relative aspect-video w-full"
                onClick={() => onMediaTap?.(message.id)}
              >
                {message.mediaThumbnail ? (
                  <img
                    src={message.mediaThumbnail}
                    alt=""
                    className="absolute inset-0 h-full w-full rounded-lg object-cover"
                  />
                ) : (
                  <video
                    src={message.mediaUrl}
                    className="absolute inset-0 h-full w-full rounded-lg object-cover"
                    preload="metadata"
                    muted
                  />
                )}
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/40 p-3 backdrop-blur-sm">
                    <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                </div>
              </button>
              {message.content && (
                <div className="mt-1 px-2 pb-1 whitespace-pre-wrap break-words text-sm" dir="auto">
                  {message.content}
                </div>
              )}
            </div>
          )}

          {message.type === 'audio' && message.mediaUrl && (
            <VoiceMessage
              audioUrl={message.mediaUrl}
              duration={(message.mediaMetadata as { duration?: number })?.duration}
              isMine={isMine}
              waveformPeaks={(message.mediaMetadata as { waveformPeaks?: number[] })?.waveformPeaks}
              timestamp={formatTime(message.createdAt, i18n.language)}
              deliveryStatus={isMine ? deriveDeliveryStatus(message) : undefined}
              senderAvatar={message.sender.avatar}
              senderName={message.sender.name}
            />
          )}

          {message.type === 'file' && message.mediaUrl && (() => {
            const meta = message.mediaMetadata as { fileName?: string; fileSize?: number; mimeType?: string } | null;
            const fileName = meta?.fileName || t('messaging.file');
            const isPdf = meta?.mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

            return (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => { e.preventDefault(); onMediaTap?.(message.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') onMediaTap?.(message.id); }}
                className="cursor-pointer [&_a]:pointer-events-none"
              >
                {isPdf ? (
                  <PdfThumbnail
                    url={message.mediaUrl!}
                    fileName={fileName}
                    fileSize={meta?.fileSize}
                    isMine={isMine}
                  />
                ) : (
                  <FileTypeIcon
                    fileName={fileName}
                    fileSize={meta?.fileSize}
                    url={message.mediaUrl!}
                    isMine={isMine}
                  />
                )}
              </div>
            );
          })()}

          {message.type === 'poll' && message.mediaMetadata && (
            <PollMessage
              pollData={message.mediaMetadata as PollData}
              messageId={message.id}
              onVote={onPollVote}
            />
          )}

          {/* Edited indicator */}
          {message.isEdited && (
            <span className={`text-[10px] italic ${isMine ? 'text-white/50' : 'text-neutral-400'}`}>
              {' '}({t('messaging.edited')})
            </span>
          )}

          {/* Time + status row — overlaid on media, inline on text/file, owned by VoiceMessage for audio */}
          {/* dir={contentDir} + justify-end = time at inline-end: right for English, left for Hebrew */}
          {!isAudioMessage && (
            <div
              dir={isMediaMessage ? undefined : contentDir}
              className={`flex items-center gap-1 ${
                isMediaMessage
                  ? 'absolute bottom-2 end-2 rounded-full bg-black/40 px-1.5 py-0.5 backdrop-blur-sm'
                  : 'mt-1 justify-end'
              }`}
            >
              {message.isStarred && (
                <svg className={`h-3 w-3 ${isMediaMessage ? 'text-yellow-300' : isMine ? 'text-yellow-300/70' : 'text-yellow-500/70'}`} viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              )}
              <span className={`text-[10px] tabular-nums ${
                isMediaMessage ? 'text-white/90' : isMine ? 'text-white/50' : 'text-neutral-400'
              }`}>
                {formatTime(message.createdAt, i18n.language)}
              </span>
              {isMine && (
                <MessageStatus
                  status={deriveDeliveryStatus(message)}
                  className={`h-3 w-3 ${isMediaMessage ? 'text-white/90' : ''}`}
                />
              )}
            </div>
          )}
        </div>

        {/* Reactions bar */}
        {hasReactions && (
          <div className={`mt-1 flex flex-wrap gap-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
            {reactionEntries.map(([emoji, userIds]) => {
              const myReaction = currentUserId ? userIds.includes(currentUserId) : false;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    chatHaptic.reaction();
                    onReaction?.(message.id, emoji, myReaction ? 'remove' : 'add');
                  }}
                  className={`flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-xs transition-all ${
                    myReaction
                      ? 'border-slate-400 bg-white/30'
                      : 'border-white/20 bg-white hover:border-white/40'
                  }`}
                >
                  <span>{emoji}</span>
                  <span className="text-[10px] font-medium tabular-nums text-neutral-800">
                    {userIds.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Quick reactions (shown on tap) */}
        {showReactions && !message.isPending && !message.isFailed && (
          <div
            className={`mt-1 flex gap-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}
          >
            <div className="flex gap-0.5 rounded-full border px-1.5 py-1 shadow-sm" style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}>
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    chatHaptic.reaction();
                    onReaction?.(message.id, emoji, 'add');
                    setShowReactions(false);
                  }}
                  className="rounded-full px-1 py-0.5 text-base transition-transform hover:scale-125 active:scale-95"
                >
                  {emoji}
                </button>
              ))}
              {/* Reply shortcut */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onReply?.(message);
                  setShowReactions(false);
                }}
                className="flex items-center rounded-full px-1.5 py-0.5 text-neutral-800 hover:bg-white/30"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 00-4-4H4" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Failed message actions */}
        {message.isFailed && (
          <div className="mt-1 flex items-center gap-2 text-xs text-error-500">
            <span>{t('messaging.sendFailed')}</span>
            <button
              type="button"
              onClick={() => onRetry?.(message.id)}
              className="font-medium underline"
            >
              {t('messaging.retry')}
            </button>
            <button
              type="button"
              onClick={() => onRemoveOptimistic?.(message.id)}
              className="font-medium underline"
            >
              {t('messaging.remove')}
            </button>
          </div>
        )}
        </motion.div>

        {/* Thread replies indicator */}
        {(message.replyCount ?? 0) > 0 && !message.threadRootId && onOpenThread && (
          <button
            type="button"
            onClick={() => onOpenThread(message)}
            className="mt-0.5 flex items-center gap-1.5 rounded-lg px-2 py-1 text-start transition-colors hover:bg-blue-50/50"
          >
            <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="text-xs font-medium text-blue-600">
              {t('messaging.threadReplies', { count: message.replyCount })}
            </span>
            {message.lastReplySenderName && (
              <span className="text-[11px] text-neutral-500">
                · {message.lastReplySenderName}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
});

export default MessageBubble;
